import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminTermsOfServiceService } from '../services/admin.termsofservice.service'
import type { TermsOfServiceSection } from '@/types'

interface UseAdminTermsOfServiceResult {
  sections: TermsOfServiceSection[]
  lastUpdated: Date | null
  loading: boolean
  error: string | null
  saving: boolean
  setSections: (sections: TermsOfServiceSection[]) => void
  save: () => Promise<void>
}

export function useAdminTermsOfService(): UseAdminTermsOfServiceResult {
  const [sections, setSections] = useState<TermsOfServiceSection[]>([])
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    adminTermsOfServiceService.get()
      .then((doc) => {
        if (doc) {
          setSections([...doc.sections].sort((a, b) => a.order - b.order))
          const raw = doc.lastUpdated as unknown as { toDate?: () => Date }
          setLastUpdated(typeof raw?.toDate === 'function' ? raw.toDate() : doc.lastUpdated)
        }
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Failed to load')))
      .finally(() => setLoading(false))
  }, [])

  const save = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      const reordered = sections.map((s, i) => ({ ...s, order: i + 1 }))
      await adminTermsOfServiceService.save(reordered)
      setSections(reordered)
      setLastUpdated(new Date())
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save'))
      throw err
    } finally {
      setSaving(false)
    }
  }, [sections])

  return { sections, lastUpdated, loading, error, saving, setSections, save }
}
